// components/consumers/documents/DocumentsTab.tsx

import { useConsumerDocuments } from "@features/cx/consumerManagement/hooks";
import { Document } from "@features/cx/consumerManagement/types";
import { GenericTable } from "@shared/components/GenericTable";
import { SortConfig, TableColumn } from "@shared/types/table";
import { Button } from "@shared/ui/button";
import { PAGE_SIZE } from "@shared/utils/constants";
import React, { useMemo, useState } from "react";

interface DocumentsTabProps {
  consumerId: string;
  consumerNo: string;
  remoteUtilityId: number;
}

export const DocumentsTab: React.FC<DocumentsTabProps> = ({
  consumerId,
  consumerNo,
  remoteUtilityId,
}) => {
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const { data, isLoading, error } = useConsumerDocuments(consumerId, {
    page: currentPage,
    limit: PAGE_SIZE,
    query: searchTerm,
    sort_field: sortConfig?.field,
    sort_direction: sortConfig?.direction,
    remote_utility_id: remoteUtilityId,
  });
  // Flatten the paginated response data
  const flattenedDocuments = useMemo(() => {
    if (!data?.results) return [];
    return data.results;
  }, [data]);

  // Define table columns
  const columns: TableColumn<Document>[] = [
    {
      key: "documentType",
      header: "Type",
      sortable: true,
    },
    {
      key: "documentSubtype",
      header: "Subtype",
      sortable: true,
    },
    {
      key: "createdDate",
      header: "Upload Date",
      sortable: true,
      render: (value) => {
        return new Date(value).toLocaleDateString();
      },
    },
    {
      key: "statusDisplay",
      header: "Status",
      sortable: true,
      type: "badge",
      badgeConfig: {
        getVariant: (value: string) => {
          switch (value) {
            case "Verified":
              return "default";
            case "Pending":
              return "secondary";
            case "Rejected":
              return "outline";
            default:
              return "default";
          }
        },
        getClassName: (value: string) => {
          switch (value) {
            case "Verified":
              return "bg-green-100 text-green-800 border-green-200 hover:bg-green-200";
            case "Pending":
              return "bg-yellow-100 text-yellow-800 border-yellow-200 hover:bg-yellow-200";
            case "Rejected":
              return "bg-red-100 text-red-800 border-red-200 hover:bg-red-200";
            default:
              return "bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200";
          }
        },
      },
    },
    {
      key: "createdUserRemoteName",
      header: "Uploaded By",
      sortable: true,
    },
    {
      key: "lastModifiedUserRemoteName",
      header: "Modified By",
      sortable: true,
    },
  ];

  // Table actions for each row
  const actions = [
    {
      label: "Download",
      icon: "Download" as const,
      onClick: (row: Document, index: number) => {
        const link = document.createElement("a");
        link.href = row.file;
        link.target = '_blank'
        link.download = row.file.split("/").pop() || "document";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      },
      variant: "ghost" as const,
    },
  ];

  // Handle sorting
  const handleSort = (field: string, direction: "asc" | "desc") => {
    setSortConfig({ field, direction });
    setCurrentPage(1);
  };

  // Handle pagination
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Handle search
  const handleSearch = (searchValue: string, paramName: string) => {
    setSearchTerm(searchValue);
    setCurrentPage(1);
  };

  // Handle row selection
  const handleSelectionChange = (newSelectedRows: string[]) => {
    setSelectedRows(newSelectedRows);
  };

  // Error state
  if (error) {
    return (
      <div className="text-center py-8 text-red-600">
        <p>{error.message}</p>
        <Button
          onClick={() => {
            setCurrentPage(1);
          }}
          className="mt-2"
          variant="outline"
        >
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Generic Table */}
      <GenericTable
        data={flattenedDocuments}
        columns={columns}
        rowKey="id"
        actions={actions}
        actionsType="icons"
        pagination={{
          currentPage: data?.page || 1,
          totalPages: data?.total_pages || 1,
          totalItems: data?.count || 0,
          itemsPerPage: data?.limit || 10,
          startItem: data?.page ? (data.page - 1) * (data.limit || 10) + 1 : 0,
          endItem: data?.page
            ? Math.min(data.page * (data.limit || 10), data.count || 0)
            : 0,
        }}
        onPageChange={handlePageChange}
        sortConfig={sortConfig}
        onSort={handleSort}
        searchPlaceholder="Search documents..."
        searchParamName="search"
        searchDebounceMs={500}
        onSearch={handleSearch}
        emptyMessage="No documents found"
      />
    </div>
  );
};
